import React, { useContext, useEffect, useState } from 'react';
import { generatePath, Link, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Breadcrumbs, Typography } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import { Capacitor } from '@capacitor/core';
import {
  StyledRescuerBreadcrumb,
  StyledRescuerTextBreadcrumb,
} from '../../rescuerDashboard/rescuerDashboardMobile/Components/StyledRescuerBreadcrumb';
import routes from '../../../routes';
import useActiveSite from '../../../hooks/useActiveSite';
import { hasAnyRoleInSite } from '../../../services/auth';
import useActiveUser from '../../../hooks/useActiveUser';
import { Roles } from '../../../models/roles';
import { fetchSiteTodos, fetchSiteTodosIfNeeded } from '../../../actions/sites';
import ToDoStack from './ToDoStack';
import { Colors } from '../../../assets/theme/Colors';
import RefreshSpinnerButton from '../../../components/RefreshSpinnerButton';

const containerSx = {
  height: '100%',
  width: '100%',
  overflow: 'auto',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
};

export const stackColors = {
  unclaimedRescues: Colors.rescues.unclaimed.color,
  unassignedRescues: Colors.schedule.color,
  pendingDonations: Colors.requestDonationAdd.color,
  pendingFoodDonors: Colors.pendingFoodDonor.color,
  newRescuer: Colors.newRescuer.color,
  substitutionRescues: Colors.substitutionRescues.color,
  irregularRescues: Colors.rescues.cancelled.color,
  reviewPickupSchedules: Colors.rescues.cancelled.color,
};

const SDToDoMobileBreadcrumbs = () => (
  <Box boxShadow={0}>
    <Breadcrumbs aria-label="breadcrumb">
      <StyledRescuerBreadcrumb
        component={Link}
        to={generatePath(routes.mobileDashboard)}
        label="Home"
        icon={<HomeIcon style={{ color: '#fff' }} fontSize="small" />}
      />
      <StyledRescuerTextBreadcrumb component="a" href="#" label="To-do" />
    </Breadcrumbs>
  </Box>
);

const SDToDoMobile = () => {
  const activeSite = useActiveSite();
  const activeUser = useActiveUser();
  const history = useHistory();
  const dispatch = useDispatch();
  const [isSpinning, setIsSpinning] = useState();
  const sdTodosEntities = useSelector((state) => state.entities.sites.sdTodos);
  const sdTodos = sdTodosEntities.bySiteId[activeSite.id];
  const unclaimedRescues = sdTodos?.unclaimedRescues || [];
  const unassignedRescues = sdTodos?.unassignedRescues || [];
  const pendingDonationRequests = sdTodos?.pendingDonationRequests || [];
  const pendingFoodDonorRegistrationRequests = sdTodos?.pendingFoodDonorRegistrationRequests || [];
  const firstRescues = sdTodos?.firstRescues || [];
  const substitutionRescues = sdTodos?.substitutionRescues || [];
  const irregularRescues = sdTodos?.irregularRescues || [];
  const reviewPickupSchedules = sdTodos?.reviewPickupSchedules || [];

  const unclaimedRescuesCount = unclaimedRescues?.length;
  const unassignedRescuesCount = unassignedRescues?.length;
  const pendingDonationRequestsCount = pendingDonationRequests?.length;
  const pendingFoodDonorRegistrationRequestsCount = pendingFoodDonorRegistrationRequests?.length;
  const firstRescuesCount = firstRescues?.length;
  const substitutionRescuesCount = substitutionRescues?.length;
  const irregularRescuesCount = irregularRescues?.length;
  const reviewPickupSchedulesCount = reviewPickupSchedules?.length;

  const isLoading = sdTodosEntities.inflight;

  const count =
    unclaimedRescuesCount +
    unassignedRescuesCount +
    pendingFoodDonorRegistrationRequestsCount +
    pendingDonationRequestsCount +
    firstRescuesCount +
    substitutionRescuesCount +
    irregularRescuesCount +
    reviewPickupSchedulesCount;

  const isSiteDirector = hasAnyRoleInSite(activeUser, activeSite.id, [
    Roles.SiteDirector,
    Roles.SiteCoordinator,
    Roles.NationalSiteDirector,
    Roles.Admin,
  ]);

  useEffect(() => {
    dispatch(fetchSiteTodosIfNeeded(activeSite.id));
  }, [dispatch, isSiteDirector]);

  const handleTodosRefresh = () => {
    if (isLoading || isSpinning) {
      return;
    }

    setIsSpinning(true);
    dispatch(fetchSiteTodos(activeSite.id));
  };

  if (!isSiteDirector) {
    return history.push(routes.index);
  }

  if (count <= 0) {
    return (
      <>
        <SDToDoMobileBreadcrumbs />
        <Box sx={containerSx}>
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              WebkitTransform: 'translate(-50%, -50%)',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography color="textSecondary" variant="h5">
              You’re all caught up, great work!
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <SDToDoMobileBreadcrumbs />

      <Box sx={containerSx}>
        {isLoading && (
          <>
            <ToDoStack isLoading={isLoading} />
            <ToDoStack isLoading={isLoading} />
          </>
        )}

        {!isLoading && substitutionRescuesCount > 0 && (
          <ToDoStack
            name="Substitution Hotlist"
            subText="Rescues that may need a last-second substitution"
            badgeCounter={substitutionRescuesCount}
            color={stackColors.substitutionRescues}
            onClick={() => history.push(routes.mobileToDoSubstitutionRescues)}
          />
        )}

        {!isLoading && unclaimedRescuesCount > 0 && (
          <ToDoStack
            name="Unclaimed Rescues"
            subText="- Next 7 Days"
            badgeCounter={unclaimedRescuesCount}
            color={stackColors.unclaimedRescues}
            onClick={() => history.push(routes.mobileToDoUnclaimedRescues)}
          />
        )}

        {!isLoading && unassignedRescuesCount > 0 && (
          <ToDoStack
            name="No Receiver Assigned"
            subText="- Next 7 Days"
            badgeCounter={unassignedRescuesCount}
            color={stackColors.unassignedRescues}
            onClick={() => history.push(routes.mobileToDoNoReceiverAssigned)}
          />
        )}

        {!isLoading && firstRescuesCount > 0 && (
          <ToDoStack
            name="First Rescue"
            subText="- New Rescuer"
            badgeCounter={firstRescuesCount}
            color={stackColors.newRescuer}
            onClick={() => history.push(routes.mobileToDoFirstRescue)}
          />
        )}

        {!isLoading && pendingDonationRequestsCount > 0 && (
          <ToDoStack
            name="Pending Donations"
            badgeCounter={pendingDonationRequestsCount}
            color={stackColors.pendingDonations}
            onClick={() => history.push(routes.pendingDonations)}
          />
        )}

        {!isLoading && irregularRescuesCount > 0 && (
          <ToDoStack
            name="Irregular Rescues"
            subText="Rescues with very high or low weights"
            badgeCounter={irregularRescuesCount}
            color={stackColors.irregularRescues}
            onClick={() => history.push(routes.mobileToDoIrregularRescues)}
          />
        )}

        {!isLoading && reviewPickupSchedulesCount > 0 && (
          <ToDoStack
            name="Adjust Pickup Schedule"
            subText="Donations with repeated no food available"
            badgeCounter={reviewPickupSchedulesCount}
            color={stackColors.reviewPickupSchedules}
            onClick={() => history.push(routes.mobileToDoReviewPickupSchedule)}
          />
        )}

        {!isLoading && pendingFoodDonorRegistrationRequestsCount > 0 && (
          <ToDoStack
            name="Pending Food Donors"
            badgeCounter={pendingFoodDonorRegistrationRequestsCount}
            color={stackColors.pendingFoodDonors}
            onClick={() => history.push(routes.pendingFoodDonorsRegistrations)}
          />
        )}

        <Box
          sx={{
            position: 'fixed',
            bottom: Capacitor.isNativePlatform() ? 'calc(32px + var(--safe-area-inset-bottom, 0px))' : 16,
            right: 16,
            zIndex: 10,
          }}
        >
          <RefreshSpinnerButton
            handleOnClick={handleTodosRefresh}
            handleOnAnimationEnd={() => setIsSpinning(false)}
            isLoading={isLoading}
            isSpinning={isSpinning}
          />
        </Box>
      </Box>
    </>
  );
};

export default SDToDoMobile;
